// Helper functions for date calculations

export function calculateNextDueDate(
  lastDueDate: Date | null,
  frequency: 'Daily' | 'Weekdays' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annual' | 'Annually' | 'Yearly',
  recurrenceDay?: number // For weekly tasks (0=Sunday, 1=Monday, etc.)
): Date {
  const baseDate = lastDueDate || new Date();
  
  console.log('[calculateNextDueDate] Input:', {
    lastDueDate: lastDueDate ? lastDueDate.toISOString() : 'null',
    baseDate: baseDate.toISOString(),
    frequency: frequency,
    recurrenceDay: recurrenceDay
  });
  
  // Create a date at UTC midnight to avoid timezone issues
  const nextDate = new Date(Date.UTC(
    baseDate.getUTCFullYear(),
    baseDate.getUTCMonth(),
    baseDate.getUTCDate(),
    0, 0, 0, 0
  ));
  
  console.log('[calculateNextDueDate] UTC midnight date:', {
    nextDate: nextDate.toISOString(),
    utcYear: baseDate.getUTCFullYear(),
    utcMonth: baseDate.getUTCMonth(),
    utcDate: baseDate.getUTCDate()
  });

  // Normalize common synonyms
  const normFreq = frequency === 'Annually' || frequency === 'Yearly' ? 'Annual' : frequency;

  switch (normFreq) {
    case 'Daily':
      const beforeDaily = nextDate.getUTCDate();
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
      console.log('[calculateNextDueDate] Daily calc:', {
        beforeDate: beforeDaily,
        afterDate: nextDate.getUTCDate(),
        result: nextDate.toISOString()
      });
      break;
      
    case 'Weekdays':
      // Move to next weekday (Monday-Friday)
      do {
        nextDate.setUTCDate(nextDate.getUTCDate() + 1);
      } while (nextDate.getUTCDay() === 0 || nextDate.getUTCDay() === 6); // Skip Sunday (0) and Saturday (6)
      break;
      
    case 'Weekly':
      if (recurrenceDay !== undefined && recurrenceDay >= 0 && recurrenceDay <= 6) {
        // Calculate next occurrence of the specific day
        const currentDay = nextDate.getUTCDay();
        let daysToAdd = recurrenceDay - currentDay;
        if (daysToAdd <= 0) {
          daysToAdd += 7; // Next week
        }
        nextDate.setUTCDate(nextDate.getUTCDate() + daysToAdd);
      } else {
        // Default to 7 days from last date
        nextDate.setUTCDate(nextDate.getUTCDate() + 7);
      }
      break;
      
    case 'Monthly':
      nextDate.setUTCMonth(nextDate.getUTCMonth() + 1);
      break;
      
    case 'Quarterly':
      nextDate.setUTCMonth(nextDate.getUTCMonth() + 3);
      break;
      
    case 'Annual':
      nextDate.setUTCFullYear(nextDate.getUTCFullYear() + 1);
      break;
  }

  console.log('[calculateNextDueDate] Final result:', {
    input: baseDate.toISOString(),
    output: nextDate.toISOString(),
    frequency: frequency
  });

  return nextDate;
}

export function getWeekdayName(day: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || 'Unknown';
}

export function formatDueDate(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(date);
  dueDate.setHours(0, 0, 0, 0);
  
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  
  return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Parse a date-like value and, for date-only strings (YYYY-MM-DD), return a Date at LOCAL midnight for that day.
 * This avoids the common timezone shift where new Date('YYYY-MM-DD') is interpreted as UTC and can appear as yesterday locally.
 */
export function parseDateOnlyToLocal(dateLike: string | Date | null | undefined): Date | null {
  if (!dateLike) return null;
  if (dateLike instanceof Date) return dateLike;
  if (typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
    const [y, m, d] = dateLike.split('-').map(n => parseInt(n, 10));
    // Construct at local midnight
    return new Date(y, (m - 1), d, 0, 0, 0, 0);
  }
  const dt = new Date(dateLike);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Safely calculate the difference in days between two dates
 * Returns 0 if either date is invalid to prevent NaN
 */
export function safeDifferenceInDays(dateLeft: Date | string | number, dateRight: Date | string | number): number {
  try {
    const left = new Date(dateLeft);
    const right = new Date(dateRight);
    
    // Check if dates are valid
    if (isNaN(left.getTime()) || isNaN(right.getTime())) {
      console.warn('Invalid date provided to safeDifferenceInDays:', { dateLeft, dateRight });
      return 0;
    }
    
    // Calculate difference in days
    const diffTime = left.getTime() - right.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating date difference:', error);
    return 0;
  }
}
